import type { WorkflowInvoiceLine, WorkflowInvoiceSourceLineCandidate } from '../../types/workflowInvoice';
import { asQuantityUnit, buildQuantitySetFromPair, INVOICE_PRINT_TOLERANCE, quantitySetToFields, scaleQuantitySetToAmount, type QuantitySet } from '../../utils/commercialQuantity';
import { extractWarehouseQuantitySetPatch, type WarehouseQuantityPatch } from '../warehouse/warehouseQuantityConversion';
import { editInvoiceAmounts } from './workflowInvoiceAmounts';

export type SourceQuantitySet = NonNullable<WorkflowInvoiceSourceLineCandidate['sourceQuantitySet']>;

/**
 * Persisted physical set of a source order/ZD line. A line saved before the
 * quantity-set era carries only `quantity` + `unit`; that single unit is its
 * whole snapshot. Returns null when nothing physical is persisted.
 */
export function sourceQuantitySetFromLine(line: {
  quantity?: number | null;
  unit?: string | null;
  quantityPcs?: number | null;
  quantityM2?: number | null;
  quantityM3?: number | null;
}): SourceQuantitySet | null {
  const set: SourceQuantitySet = {
    pcs: line.quantityPcs ?? null,
    m2: line.quantityM2 ?? null,
    m3: line.quantityM3 ?? null,
  };
  if (set.pcs != null || set.m2 != null || set.m3 != null) return set;
  const unit = asQuantityUnit(line.unit);
  if (unit && line.quantity != null && Number(line.quantity) > 0) {
    return { ...set, [unit]: Number(line.quantity) };
  }
  return null;
}

/**
 * Drops every derived unit the source line never persisted. The server rejects
 * such units (`INVOICE_QUANTITY_UNRESOLVED`) instead of backfilling them from
 * catalog geometry. Without a known source set the derived set is kept.
 */
export function restrictQuantitySetToSource<T extends Pick<WorkflowInvoiceLine, 'quantityPcs' | 'quantityM2' | 'quantityM3'>>(
  fields: T,
  sourceSet: SourceQuantitySet | null | undefined,
): T {
  if (!sourceSet) return fields;
  return {
    ...fields,
    quantityPcs: sourceSet.pcs == null ? null : fields.quantityPcs,
    quantityM2: sourceSet.m2 == null ? null : fields.quantityM2,
    quantityM3: sourceSet.m3 == null ? null : fields.quantityM3,
  };
}

export function hasInvoicePhysicalQuantitySet(line: Pick<WorkflowInvoiceLine, 'quantityPcs' | 'quantityM2' | 'quantityM3'>): boolean {
  return [line.quantityPcs, line.quantityM2, line.quantityM3].some(value => value != null);
}

/** New modal values carry a pair; this adapter owns its first persisted set. */
export function invoiceQuantityPatch(
  line: WorkflowInvoiceLine,
  patch: WarehouseQuantityPatch,
  sourceSet?: SourceQuantitySet | null,
): Partial<WorkflowInvoiceLine> {
  const existingSet = extractWarehouseQuantitySetPatch(patch);
  const acceptedPair = {
    physicalQuantity: patch.quantity,
    physicalUnit: asQuantityUnit(patch.unit),
    pricingQuantity: patch.pricingQuantity ?? patch.quantity,
    pricingUnit: asQuantityUnit(patch.pricingUnit ?? patch.unit),
    conversionUnit: patch.conversionUnit,
    conversionQuantity: patch.conversionQuantity ?? undefined,
  };
  const firstSet = hasInvoicePhysicalQuantitySet(existingSet)
    ? null
    : buildQuantitySetFromPair(acceptedPair, line);
  // A new amount is a new reading of the invoice: whole pieces when it is
  // their rounding; an earlier confirmed rounding no longer applies.
  return {
    ...snapToWholePieces({
      quantity: patch.quantity,
      unit: acceptedPair.physicalUnit,
      ...restrictQuantitySetToSource(firstSet ? quantitySetToFields(firstSet) : existingSet, sourceSet),
    }, sourceSet),
    conversionUnit: patch.conversionUnit,
    conversionQuantity: patch.conversionQuantity ?? null,
    commercialQuantity: patch.pricingQuantity ?? null,
    commercialUnit: patch.pricingUnit ?? null,
    piecesRoundedFrom: null,
  };
}

// ---------------------------------------------------------------------------
// WP11 (docs/decisions/2026-09-24-wp11-faktury-czesciowe.md): partial
// invoices. A source line carries its uninvoiced remainder from the order's
// `invoiceCoverage`; a newly linked invoice line without a quantity is
// prefilled with that remainder (the operator may lower it for a partial
// invoice or raise it — over-invoicing asks for the explicit order alignment
// at approval).

const PHYSICAL_UNITS = ['pcs', 'm2', 'm3'] as const;
type PhysicalUnit = typeof PHYSICAL_UNITS[number];
const isPhysicalUnit = (unit: unknown): unit is PhysicalUnit => (PHYSICAL_UNITS as readonly unknown[]).includes(unit);
const roundQuantity = (value: number) => Math.round(value * 1000) / 1000;

/** Coverage of a source order line (subset of the coverage read model). */
export interface SourceLineCoverage {
  unit: string | null;
  remainingQuantity: number | null;
}

/**
 * Adds the uninvoiced remainder (display quantity + scaled physical set) to a
 * candidate. Unknown coverage or quantities leave the candidate unchanged.
 */
export function withCoverageRemainder(
  candidate: WorkflowInvoiceSourceLineCandidate,
  coverage: SourceLineCoverage | null | undefined,
): WorkflowInvoiceSourceLineCandidate {
  const set = candidate.sourceQuantitySet;
  if (!coverage || !set || !isPhysicalUnit(coverage.unit) || coverage.remainingQuantity == null) return candidate;
  const ordered = set[coverage.unit];
  if (ordered == null || ordered <= 0) return candidate;
  const ratio = coverage.remainingQuantity / ordered;
  const scale = (value: number | null) => (value == null ? null : roundQuantity(value * ratio));
  return {
    ...candidate,
    remainingQuantity: coverage.remainingQuantity,
    remainingUnit: coverage.unit,
    remainingQuantitySet: { pcs: scale(set.pcs), m2: scale(set.m2), m3: scale(set.m3) },
  };
}

/**
 * True when the line's quantity (in the remainder's unit) exceeds the source
 * line's uninvoiced remainder — over-invoicing, shown as a hint only
 * (approval asks for the explicit order alignment). Unknown → false.
 */
export function exceedsRemainder(
  line: Pick<WorkflowInvoiceLine, 'quantity' | 'unit' | 'quantityPcs' | 'quantityM2' | 'quantityM3'>,
  candidate: WorkflowInvoiceSourceLineCandidate | null | undefined,
): boolean {
  const unit = candidate?.remainingUnit;
  const remaining = candidate?.remainingQuantity;
  if (!candidate?.remainingQuantitySet || !isPhysicalUnit(unit) || remaining == null) return false;
  const field = ({ pcs: 'quantityPcs', m2: 'quantityM2', m3: 'quantityM3' } as const)[unit];
  const quantity = line[field] ?? (line.unit === unit ? line.quantity : null);
  return quantity != null && quantity > remaining + 0.0005;
}

/**
 * Quantity fields for a line just linked to `candidate`: its uninvoiced
 * remainder, physical set and commercial pair (the source's price unit when
 * that unit is in the set). null when the line already has a quantity or the
 * remainder is unknown or zero — nothing is overwritten.
 */
export function remainderPrefill(
  line: Pick<WorkflowInvoiceLine, 'quantity' | 'quantityPcs' | 'quantityM2' | 'quantityM3'>,
  candidate: WorkflowInvoiceSourceLineCandidate,
): Partial<WorkflowInvoiceLine> | null {
  if (line.quantity != null || hasInvoicePhysicalQuantitySet(line)) return null;
  const set = candidate.remainingQuantitySet;
  const unit = candidate.remainingUnit;
  const quantity = candidate.remainingQuantity;
  if (!set || !isPhysicalUnit(unit) || quantity == null || quantity <= 0 || set[unit] == null) return null;
  const pricingUnit: PhysicalUnit = isPhysicalUnit(candidate.sourcePricingUnit) && set[candidate.sourcePricingUnit] != null
    ? candidate.sourcePricingUnit
    : unit;
  return {
    quantity,
    unit,
    quantityPcs: set.pcs,
    quantityM2: set.m2,
    quantityM3: set.m3,
    conversionUnit: null,
    conversionQuantity: null,
    commercialQuantity: pricingUnit === unit ? quantity : set[pricingUnit],
    commercialUnit: pricingUnit,
  };
}

// ---------------------------------------------------------------------------
// Whole pieces (owner 2026-10-22). Mirror of backend
// invoiceImportValidation (wholePieceSet / confirmedPieceRounding). OMS keeps
// quantities to 0,001 so that szt./m²/m³ convert consistently; an invoice
// prints m² rounded to 0,01. A printed amount within the tolerance of whole
// pieces IS those pieces (717,19 m² of a 2,86875-m² board = 250 szt. /
// 717,188 m²); a larger gap is a real fractional piece (720,00 m² = 250,98
// szt.) that the operator may round explicitly. The commercial quantity and
// amounts always stay as printed.

const PRINT_TOLERANCE = INVOICE_PRINT_TOLERANCE;
const QUANTITY_FIELD = { pcs: 'quantityPcs', m2: 'quantityM2', m3: 'quantityM3' } as const;
type QuantityFields = Pick<WorkflowInvoiceLine, 'quantityPcs' | 'quantityM2' | 'quantityM3'>;
type RoundableLine = QuantityFields & Pick<WorkflowInvoiceLine, 'unit' | 'commercialQuantity' | 'commercialUnit' | 'piecesRoundedFrom'>;

function piecesSet(sourceSet: SourceQuantitySet, pieces: number) {
  return scaleQuantitySetToAmount(sourceSet as QuantitySet, pieces, 'pcs');
}

/** The line's physical fields snapped to whole pieces; null when they are not a rounding of whole pieces. */
export function wholePieceFields(line: QuantityFields, sourceSet: SourceQuantitySet | null | undefined): QuantityFields | null {
  if (!sourceSet || !(Number(sourceSet.pcs) > 0) || line.quantityPcs == null) return null;
  const pieces = Math.round(line.quantityPcs);
  const set = pieces > 0 ? piecesSet(sourceSet, pieces) : null;
  if (!set) return null;
  const agrees = PHYSICAL_UNITS.every((unit) => {
    const value = line[QUANTITY_FIELD[unit]];
    return value == null || (set[unit] != null && Math.abs(value - Number(set[unit])) <= PRINT_TOLERANCE[unit] + 1e-9);
  });
  return agrees ? { quantityPcs: set.pcs, quantityM2: set.m2, quantityM3: set.m3 } : null;
}

/**
 * The invoice is the source of truth (decision 2026-10-30-faktura-zrodlem-prawdy;
 * mirror of backend invoiceImportValidation.statedPieceSet): a whole piece count
 * on a line priced in m²/m³ — printed on the invoice („65 pcs”) or entered — is
 * the physical quantity in the source's geometry (65 szt. = 23,563 m²); the
 * printed amount (23,53 m²) stays the commercial quantity. null when not applicable.
 */
export function statedPieceFields(
  line: QuantityFields & Pick<WorkflowInvoiceLine, 'commercialUnit'>,
  sourceSet: SourceQuantitySet | null | undefined,
): QuantityFields | null {
  if (line.commercialUnit !== 'm2' && line.commercialUnit !== 'm3') return null;
  const pieces = line.quantityPcs;
  if (!sourceSet || !(Number(sourceSet.pcs) > 0) || pieces == null || !Number.isInteger(pieces) || pieces <= 0) return null;
  const set = piecesSet(sourceSet, pieces);
  return set ? { quantityPcs: set.pcs, quantityM2: set.m2, quantityM3: set.m3 } : null;
}

/** Physical fields + entered amount of `fields` snapped to whole pieces (unchanged when not applicable). */
export function snapToWholePieces<T extends QuantityFields & Pick<WorkflowInvoiceLine, 'unit' | 'quantity'>>(
  fields: T,
  sourceSet: SourceQuantitySet | null | undefined,
): T {
  const whole = wholePieceFields(fields, sourceSet);
  if (!whole) return fields;
  const unit = isPhysicalUnit(fields.unit) ? fields.unit : null;
  return { ...fields, ...whole, ...(unit && whole[QUANTITY_FIELD[unit]] != null ? { quantity: whole[QUANTITY_FIELD[unit]] } : {}) };
}

/** Exact piece count of the printed commercial amount (m²/m³), as the backend checks it. */
function printedPieces(line: RoundableLine, sourceSet: SourceQuantitySet): number | null {
  const unit = line.commercialUnit;
  if ((unit !== 'm2' && unit !== 'm3') || line.commercialQuantity == null) return null;
  return scaleQuantitySetToAmount(sourceSet as QuantitySet, line.commercialQuantity, unit)?.pcs ?? null;
}

/** A real fractional piece count the operator may round: `exact` szt. -> `whole` szt.; null otherwise. */
export function fractionalPieces(
  line: RoundableLine,
  sourceSet: SourceQuantitySet | null | undefined,
): { exact: number; whole: number } | null {
  if (line.piecesRoundedFrom != null || !sourceSet || !(Number(sourceSet.pcs) > 0) || line.quantityPcs == null) return null;
  const exact = printedPieces(line, sourceSet);
  if (exact == null || Math.abs(exact - Math.round(exact)) < 1e-9 || wholePieceFields(line, sourceSet)) return null;
  return { exact, whole: Math.max(1, Math.round(exact)) };
}

/** Operator-confirmed rounding: physical set = `whole` szt., the printed count is kept in piecesRoundedFrom. */
export function roundToWholePieces(
  line: RoundableLine,
  sourceSet: SourceQuantitySet,
  rounding: { exact: number; whole: number },
): Partial<WorkflowInvoiceLine> {
  const set = piecesSet(sourceSet, rounding.whole);
  if (!set) return {};
  const unit = isPhysicalUnit(line.unit) ? line.unit : null;
  return {
    quantityPcs: set.pcs,
    quantityM2: set.m2,
    quantityM3: set.m3,
    ...(unit && set[unit] != null ? { quantity: set[unit] } : {}),
    piecesRoundedFrom: roundQuantity(rounding.exact),
  };
}

/** Undo a confirmed rounding: back to the printed piece count. */
export function undoPieceRounding(line: RoundableLine, sourceSet: SourceQuantitySet): Partial<WorkflowInvoiceLine> {
  const set = line.piecesRoundedFrom == null ? null : piecesSet(sourceSet, line.piecesRoundedFrom);
  if (!set) return { piecesRoundedFrom: null };
  const unit = isPhysicalUnit(line.unit) ? line.unit : null;
  return {
    quantityPcs: set.pcs,
    quantityM2: set.m2,
    quantityM3: set.m3,
    ...(unit && set[unit] != null ? { quantity: set[unit] } : {}),
    piecesRoundedFrom: null,
  };
}

/**
 * The printed amount differs from the physical one only by the invoice's
 * rounding (717,19 m² on the invoice, 717,188 m² = 250 szt. in OMS): shown as
 * an explanation, never as a problem.
 */
export function printRoundingNote(line: RoundableLine): { printed: number; physical: number; unit: 'm2' | 'm3'; pieces: number; stated: boolean } | null {
  const unit = line.commercialUnit;
  if ((unit !== 'm2' && unit !== 'm3') || line.commercialQuantity == null || line.piecesRoundedFrom != null) return null;
  const physical = line[QUANTITY_FIELD[unit]];
  const pieces = line.quantityPcs;
  if (physical == null || pieces == null || Math.abs(pieces - Math.round(pieces)) > 1e-9) return null;
  const gap = Math.abs(physical - line.commercialQuantity);
  // `stated`: a larger gap next to whole pieces is the invoice's own amount
  // (65 szt. and 23,53 m² both printed), not a rounding to explain.
  return gap > 1e-9
    ? { printed: line.commercialQuantity, physical, unit, pieces, stated: gap > PRINT_TOLERANCE[unit] + 1e-9 }
    : null;
}

/**
 * FZ.2 (owner 2026-10-30): the amount to pay differs from the line's whole
 * pieces by more than the invoice's print rounding (65 szt. = 23,563 m², on the
 * invoice 23,53 m² — or a typo 2353 m²). Saving asks once which amount stays.
 */
export function quantityGap(line: RoundableLine): { printed: number; physical: number; unit: 'm2' | 'm3'; pieces: number } | null {
  const note = printRoundingNote(line);
  return note?.stated ? { printed: note.printed, physical: note.physical, unit: note.unit, pieces: note.pieces } : null;
}

/** Identity of an answered gap: the same line with the same pieces and amount is not asked again. */
export function quantityGapKey(line: RoundableLine & Pick<WorkflowInvoiceLine, 'key' | 'id'>): string {
  return [line.id ?? line.key, line.quantityPcs, line.commercialQuantity, line.commercialUnit].join('|');
}

/**
 * „Zapisz z wymiarów”: the amount to pay becomes the pieces in the line's
 * dimensions, applied like a typed quantity — the net amount stays as on the
 * invoice (with net purchase prices the unit price is recomputed from it).
 */
export function withDimensionQuantity(line: WorkflowInvoiceLine, netPurchasePrices: boolean): WorkflowInvoiceLine {
  const gap = quantityGap(line);
  return gap ? editInvoiceAmounts(line, { commercialQuantity: gap.physical }, netPurchasePrices) : line;
}
